import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

/**
 * Utility to log HTTP requests to the monitoring system
 */
export const logRequest = async (
  method: string,
  path: string,
  statusCode: number,
  durationMs: number = 0,
  userId?: string,
  requestBody?: any,
  responseBody?: any
) => {
  try {
    await supabase.from('request_logs').insert({
      method,
      path,
      status_code: statusCode,
      duration_ms: durationMs,
      user_id: userId,
      request_body: requestBody,
      response_body: responseBody,
      timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Failed to log request:', error);
  }
};

/**
 * Utility to log errors to the monitoring system
 */
export const logError = async (
  message: string,
  severity: 'info' | 'warning' | 'error' | 'critical' = 'error',
  route?: string,
  stackTrace?: string,
  userId?: string,
  context?: any
) => {
  try {
    await supabase.from('error_logs').insert({
      message,
      severity,
      route,
      stack_trace: stackTrace,
      user_id: userId,
      context,
      timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Failed to log error:', error);
  }
};

/**
 * Hook to automatically log page views
 */
export const usePageViewLogger = (pageName: string) => {
  const { user } = useAuth();

  useEffect(() => {
    const startTime = Date.now();

    // Log page view
    logRequest('GET', `/app/${pageName}`, 200, 0, user?.id);

    return () => {
      // Log time spent on page on unmount
      const duration = Date.now() - startTime;
      if (duration > 1000) { // Only log if spent more than 1 second
        logRequest('VIEW', `/app/${pageName}`, 200, duration, user?.id);
      }
    };
  }, [pageName, user?.id]);
};

/**
 * Wrapper for fetch that logs requests
 */
export const loggedFetch = async (
  url: string,
  options: RequestInit = {},
  userId?: string
): Promise<Response> => {
  const startTime = Date.now();
  const method = options.method || 'GET';
  
  try {
    const response = await fetch(url, options);
    const duration = Date.now() - startTime;
    
    // Log the request
    await logRequest(
      method,
      url,
      response.status,
      duration,
      userId,
      options.body ? JSON.parse(options.body as string) : undefined
    );
    
    return response;
  } catch (error: any) {
    const duration = Date.now() - startTime;
    
    // Log error
    await logError(
      error.message || 'Network error',
      'error',
      url,
      error.stack,
      userId
    );
    
    await logRequest(method, url, 0, duration, userId);
    
    throw error;
  }
};

/**
 * Global error handler setup
 */
export const setupGlobalErrorHandler = (userId?: string) => {
  if (typeof window === 'undefined') return;

  window.onerror = (message, source, lineno, colno, error) => {
    logError(
      String(message),
      'error',
      source || window.location.pathname,
      error?.stack,
      userId,
      { lineno, colno }
    );
  };

  window.onunhandledrejection = (event) => {
    logError(
      event.reason?.message || 'Unhandled Promise Rejection',
      'error',
      window.location.pathname,
      event.reason?.stack,
      userId
    );
  };
};
